import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { formatCurrency } from "../../lib/romEngine";
import {
    UsersThree, Plus, PencilSimple, Trash, X, CircleNotch, MagnifyingGlass, QrCode, ArrowsClockwise, Printer,
} from "@phosphor-icons/react";

const BLANK = {
    employee_name: "", role: "", team: "", cost_rate_per_hour: "", annual_salary: "",
    billable_rate_per_hour: "", is_active: true,
};

export default function TimesheetEmployees() {
    const { user } = useAuth();
    const isManage = ["admin", "manager"].includes(user?.role);
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [modal, setModal] = useState(null); // {edit, form}
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [qrEmp, setQrEmp] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const { data } = await endpoints.employees({ active_only: false });
            setList(data || []);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const filtered = list.filter((e) =>
        !search || e.employee_name?.toLowerCase().includes(search.toLowerCase())
        || e.role?.toLowerCase().includes(search.toLowerCase())
        || e.team?.toLowerCase().includes(search.toLowerCase()));

    function openNew() { setModal({ edit: null, form: { ...BLANK } }); setErr(""); }
    function openEdit(e) {
        setModal({ edit: e, form: {
            employee_name: e.employee_name || "", role: e.role || "", team: e.team || "",
            cost_rate_per_hour: e.cost_rate_per_hour ?? "", annual_salary: e.annual_salary ?? "",
            billable_rate_per_hour: e.billable_rate_per_hour ?? "", is_active: e.is_active !== false,
        } });
        setErr("");
    }

    async function save() {
        const f = modal.form;
        if (!f.employee_name.trim()) { setErr("Name is required"); return; }
        setSaving(true); setErr("");
        const payload = {
            employee_name: f.employee_name.trim(), role: f.role, team: f.team,
            annual_salary: f.annual_salary ? Number(f.annual_salary) : null,
            cost_rate_per_hour: f.cost_rate_per_hour ? Number(f.cost_rate_per_hour) : undefined,
            billable_rate_per_hour: f.billable_rate_per_hour ? Number(f.billable_rate_per_hour) : null,
            is_active: f.is_active,
        };
        try {
            if (modal.edit) await endpoints.updateEmployee(modal.edit.id, payload);
            else await endpoints.saveEmployee(payload);
            setModal(null);
            await load();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setSaving(false); }
    }

    async function remove(e) {
        if (!window.confirm(`Delete ${e.employee_name}? This cannot be undone.`)) return;
        try { await endpoints.deleteEmployee(e.id); await load(); }
        catch (er) { alert(formatApiError(er)); }
    }

    return (
        <div className="max-w-[1400px] mx-auto px-6 py-8 space-y-6" data-testid="employees-page">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">Timesheets</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <UsersThree size={28} weight="bold" className="text-[var(--rom-orange)]" /> Employees
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1">Your workforce + cost rates used to value timesheet hours.</p>
                </div>
                {isManage && (
                    <button className="btn btn-primary" onClick={openNew} data-testid="add-employee-btn">
                        <Plus size={14} weight="bold" /> Add employee
                    </button>
                )}
            </header>

            <div className="rom-card">
                <div className="rom-card-head">
                    <div className="relative w-full max-w-xs">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rom-text-3)]" />
                        <input
                            data-testid="employee-search"
                            value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name, role, team…"
                            className="w-full h-9 pl-9 pr-3 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none"
                        />
                    </div>
                    <span className="text-xs text-[var(--rom-text-3)]">{filtered.length} of {list.length}</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="rom-table">
                        <thead>
                            <tr>
                                <th>Name</th><th>Role</th><th>Team</th>
                                <th className="numeric">Cost / hr</th><th className="numeric">Billable / hr</th>
                                <th>Status</th><th>Badge</th>{isManage && <th></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={isManage ? 8 : 7} className="text-center py-10 text-[var(--rom-text-3)]">Loading…</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={isManage ? 8 : 7} className="text-center py-10 text-[var(--rom-text-3)]">No employees.</td></tr>
                            ) : filtered.map((e) => (
                                <tr key={e.id} data-testid={`employee-row-${e.id}`}>
                                    <td className="font-semibold">{e.employee_name}</td>
                                    <td className="text-[var(--rom-text-2)]">{e.role || "—"}</td>
                                    <td className="text-[var(--rom-text-2)]">{e.team || "—"}</td>
                                    <td className="numeric font-mono">{formatCurrency(e.cost_rate_per_hour)}</td>
                                    <td className="numeric font-mono">{e.billable_rate_per_hour ? formatCurrency(e.billable_rate_per_hour) : "—"}</td>
                                    <td><span className={`chip ${e.is_active !== false ? "chip-green" : "chip-gray"}`}>{e.is_active !== false ? "Active" : "Inactive"}</span></td>
                                    <td>
                                        {isManage ? (
                                            <button onClick={() => setQrEmp(e)} data-testid={`emp-qr-btn-${e.id}`} className="p-1.5 rounded text-[var(--rom-navy)] hover:bg-slate-100" title="View / print QR badge"><QrCode size={16} weight="bold" /></button>
                                        ) : "—"}
                                    </td>
                                    {isManage && (
                                        <td>
                                            <div className="flex gap-1 justify-end">
                                                <button onClick={() => openEdit(e)} data-testid={`edit-employee-${e.id}`} className="p-1.5 rounded text-[var(--rom-text-2)] hover:bg-slate-100" title="Edit"><PencilSimple size={15} /></button>
                                                <button onClick={() => remove(e)} data-testid={`delete-employee-${e.id}`} className="p-1.5 rounded text-red-500 hover:bg-red-50" title="Delete"><Trash size={15} /></button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {modal && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !saving && setModal(null)}>
                    <div className="bg-white rounded-lg w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="employee-modal">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rom-border)]">
                            <h3 className="font-display font-bold text-lg">{modal.edit ? "Edit employee" : "Add employee"}</h3>
                            <button onClick={() => setModal(null)} className="p-1 text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"><X size={18} /></button>
                        </div>
                        <div className="p-5 grid grid-cols-2 gap-4">
                            <Field label="Full name *" span2>
                                <input data-testid="emp-name" value={modal.form.employee_name} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, employee_name: e.target.value } }))} className={inputCls} />
                            </Field>
                            <Field label="Role"><input data-testid="emp-role" value={modal.form.role} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, role: e.target.value } }))} className={inputCls} placeholder="e.g. Restoration Tech" /></Field>
                            <Field label="Team"><input data-testid="emp-team" value={modal.form.team} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, team: e.target.value } }))} className={inputCls} placeholder="e.g. Field Crew" /></Field>
                            <Field label="Annual salary ($)"><input data-testid="emp-salary" type="number" value={modal.form.annual_salary} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, annual_salary: e.target.value } }))} className={inputCls} placeholder="auto → hourly" /></Field>
                            <Field label="Cost rate / hr ($)"><input data-testid="emp-cost-rate" type="number" value={modal.form.cost_rate_per_hour} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, cost_rate_per_hour: e.target.value } }))} className={inputCls} placeholder="35.00" /></Field>
                            <Field label="Billable rate / hr ($)"><input data-testid="emp-billable" type="number" value={modal.form.billable_rate_per_hour} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, billable_rate_per_hour: e.target.value } }))} className={inputCls} placeholder="optional" /></Field>
                            <Field label="Active">
                                <label className="flex items-center gap-2 h-9 text-sm">
                                    <input type="checkbox" checked={modal.form.is_active} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, is_active: e.target.checked } }))} data-testid="emp-active" />
                                    Employee is active
                                </label>
                            </Field>
                        </div>
                        {err && <div className="px-5 pb-2 text-sm text-red-600">{err}</div>}
                        <div className="px-5 py-4 border-t border-[var(--rom-border)] flex justify-end gap-2">
                            <button className="btn btn-ghost" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
                            <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="save-employee-btn">
                                {saving ? <CircleNotch size={14} className="animate-spin" /> : null} Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {qrEmp && <EmployeeQrModal employee={qrEmp} onClose={() => setQrEmp(null)} />}
        </div>
    );
}

function EmployeeQrModal({ employee, onClose }) {
    const [regenerating, setRegenerating] = useState(false);
    const [bust, setBust] = useState(0);
    const [token, setToken] = useState(employee.clock_token);
    const [copied, setCopied] = useState(false);

    const shareLink = `${window.location.origin}/clock-in?code=BR-EMP:${token}`;

    async function regenerate() {
        if (!window.confirm("Regenerate this badge? The old QR code will stop working.")) return;
        setRegenerating(true);
        try {
            const { data } = await endpoints.regenerateEmployeeToken(employee.id);
            setToken(data.clock_token);
            setBust((b) => b + 1);
        }
        catch (e) { alert(formatApiError(e)); }
        finally { setRegenerating(false); }
    }

    function copyLink() {
        navigator.clipboard.writeText(shareLink).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg w-full max-w-xs shadow-xl text-center" onClick={(e) => e.stopPropagation()} data-testid="employee-qr-modal">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rom-border)]">
                    <h3 className="font-display font-bold text-base">{employee.employee_name}'s badge</h3>
                    <button onClick={onClose} className="p-1 text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <img src={`${endpoints.employeeQrUrl(employee.id)}&v=${bust}`} alt="Employee QR badge" className="mx-auto w-48 h-48 border border-[var(--rom-border)] rounded" data-testid="employee-qr-image" />
                    <p className="text-xs text-[var(--rom-text-3)]">Print this badge or post it at the job site. Scanning it identifies {employee.employee_name} at clock-in — no login required.</p>
                    <div className="flex gap-1.5">
                        <input readOnly value={shareLink} onClick={(e) => e.target.select()} data-testid="employee-share-link-input"
                            className="flex-1 h-9 px-2 rounded-[4px] border border-[var(--rom-border)] text-xs font-mono bg-slate-50 truncate" />
                        <button onClick={copyLink} data-testid="employee-share-link-copy" className="btn btn-ghost shrink-0 px-3">
                            {copied ? "Copied" : "Copy"}
                        </button>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => window.print()} className="btn btn-ghost flex-1 justify-center" data-testid="employee-qr-print"><Printer size={14} weight="bold" /> Print</button>
                        <button onClick={regenerate} disabled={regenerating} className="btn btn-ghost flex-1 justify-center text-red-600" data-testid="employee-qr-regenerate">
                            {regenerating ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />} Regenerate
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const inputCls = "w-full h-9 px-3 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none";

function Field({ label, children, span2 }) {
    return (
        <div className={span2 ? "col-span-2" : ""}>
            <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">{label}</label>
            {children}
        </div>
    );
}
